import { useMsal } from '@azure/msal-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  PreSalesCancellationReason,
  PreSalesQuestion,
  PreSalesQualification,
} from '../../../dto/model';
import { scopes } from '../../../authConfig';
import { heatBookingPageService } from '../../../services/heat-booking-page-service';
import { GetAccessToken } from '../../../utils/auth-utils';
import {
  isUnifiedPreSalesProductEnabled,
  isUnifiedPreSalesQuestionEnabled,
} from '../../../utils/unified-pre-sales-pv';
import {
  isPvOnlyInterest,
  isPvOnlyHeatCancellationReason,
  isPvOrSharedQuestion,
} from '../pre-sales-pv-handover';
import {
  evaluatePreSalesQuestionValidation,
  evaluatePreSalesQuestionVisible,
  isPreSalesAnswerEmpty,
  PreSalesAnswers,
} from '../../../utils/pre-sales-questionnaire-evaluator';
import {
  applyPreSalesDerivedAnswers,
  clearNewlyHiddenPreSalesAnswers,
  getPreSalesCustomerInfoPatch,
  getPreSalesCancellationProjection,
  getStablePreSalesAnswersKey,
  scrollToPreSalesQuestion,
  serializePreSalesAnswers,
  serializePreSalesQualificationAnswers,
} from './pre-sales-questionnaire-utils';
import type {
  PreSalesCancellationEvaluationStatus,
  PreSalesCustomerInfoPatch,
} from './pre-sales-questionnaire-types';

type UsePreSalesQuestionnaireFormParams = {
  questions: PreSalesQuestion[];
  hpOrderNumber?: string;
  signupId?: string;
  formId?: string;
  unifiedFlow?: boolean;
  isLocked?: boolean;
  onCustomerInfoChange?: (patch: PreSalesCustomerInfoPatch) => void;
};

const cancellationEvaluationRequests = new Map<string, Promise<PreSalesCancellationReason[] | null>>();
const cancellationEvaluationResults = new Map<string, PreSalesCancellationReason[]>();
const MAX_CANCELLATION_EVALUATION_RESULTS = 100;

const getOrdinaryHpCancellationReasons = (
  cancellationReasons: PreSalesCancellationReason[],
): PreSalesCancellationReason[] => cancellationReasons.filter(
  (reason) => reason.product === 'WP' && !isPvOnlyHeatCancellationReason(reason),
);

const cacheCancellationEvaluationResult = (
  requestKey: string,
  result: PreSalesCancellationReason[],
) => {
  cancellationEvaluationResults.delete(requestKey);
  cancellationEvaluationResults.set(requestKey, result);

  if (cancellationEvaluationResults.size > MAX_CANCELLATION_EVALUATION_RESULTS) {
    const oldestKey = cancellationEvaluationResults.keys().next().value;
    if (oldestKey !== undefined) {
      cancellationEvaluationResults.delete(oldestKey);
    }
  }
};

export const usePreSalesQuestionnaireForm = ({
  questions: sourceQuestions,
  hpOrderNumber,
  signupId,
  formId,
  unifiedFlow = false,
  isLocked = false,
  onCustomerInfoChange,
}: UsePreSalesQuestionnaireFormParams) => {
  const questions = useMemo(
    () => unifiedFlow ? sourceQuestions.filter(isUnifiedPreSalesQuestionEnabled) : sourceQuestions,
    [sourceQuestions, unifiedFlow],
  );
  const { instance, inProgress } = useMsal();
  const [qualification, setQualification] = useState<PreSalesQualification>();
  const [answers, setAnswers] = useState<PreSalesAnswers>({});
  const [answersSourceQuestions, setAnswersSourceQuestions] = useState<PreSalesQuestion[] | null>(null);
  const [touchedQuestions, setTouchedQuestions] = useState<Record<string, boolean>>({});
  const [cancellationReasons, setCancellationReasons] = useState<PreSalesCancellationReason[]>([]);
  const [answeredQuestionKeysAtCancellation, setAnsweredQuestionKeysAtCancellation] =
    useState<ReadonlySet<string>>(new Set());
  const [isCancellationModalDismissed, setIsCancellationModalDismissed] = useState(false);
  const [hasCompletedCancellationEvaluation, setHasCompletedCancellationEvaluation] = useState(false);
  const [isEvaluatingCancellation, setIsEvaluatingCancellation] = useState(false);
  const [cancellationEvaluationFailed, setCancellationEvaluationFailed] = useState(false);
  const lastEvaluatedKeyRef = useRef<string | null>(null);
  const lastOrdinaryHpCancellationKeyRef = useRef<string | null>(null);
  const requestSeqRef = useRef(0);
  const pendingQualificationRequestsRef = useRef(new Set<Promise<unknown>>());
  const hasRunInitialEvaluationRef = useRef(false);
  const isMountedRef = useRef(false);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    const initialAnswers = questions.reduce<PreSalesAnswers>((acc, question) => {
      acc[question.questionKey] = question.answer ?? question.defaultValue ?? undefined;
      return acc;
    }, {});
    setAnswers(initialAnswers);
    setAnswersSourceQuestions(questions);
    setTouchedQuestions({});
    setCancellationReasons([]);
    setQualification(undefined);
    setAnsweredQuestionKeysAtCancellation(new Set());
    setHasCompletedCancellationEvaluation(false);
    setCancellationEvaluationFailed(false);
    setIsCancellationModalDismissed(false);
    setIsEvaluatingCancellation(false);
    requestSeqRef.current += 1;
    lastEvaluatedKeyRef.current = null;
    lastOrdinaryHpCancellationKeyRef.current = null;
    hasRunInitialEvaluationRef.current = false;
  }, [questions, signupId, formId, unifiedFlow]);

  const evaluateCancellation = useCallback(
    async (nextAnswers: PreSalesAnswers, lastChangedKey?: string) => {
      if (isLocked || (!unifiedFlow && !hpOrderNumber)) return;

      const nextKey = getStablePreSalesAnswersKey(nextAnswers);
      const requestKey = `${hpOrderNumber}:${lastChangedKey ?? ''}:${nextKey}`;
      // Opening the cancellation dialog blurs the focused field. Reuse the current
      // qualification so that focus changes cannot clear and reopen the dialog.
      if (unifiedFlow && nextKey === lastEvaluatedKeyRef.current) return;
      if (!unifiedFlow && nextKey === lastEvaluatedKeyRef.current && cancellationEvaluationResults.has(requestKey)) return;

      const seq = ++requestSeqRef.current;
      setHasCompletedCancellationEvaluation(false);
      setIsEvaluatingCancellation(true);
      setCancellationEvaluationFailed(false);
      setCancellationReasons([]);

      try {
        let data: PreSalesCancellationReason[] | null = null;

        if (unifiedFlow) {
          const request = (async () => {
            const token = await GetAccessToken(instance, inProgress, scopes.salesAppointmentApi);
            return heatBookingPageService.qualifyPreSalesForm({
              formId: formId || null,
              formIdentifier: { signupId: signupId || null, crmId: null, hpOrderNumber: null },
              formValues: serializePreSalesQualificationAnswers(questions, nextAnswers),
            }, token.accessToken);
          })();
          pendingQualificationRequestsRef.current.add(request);
          const response = await request.finally(() => {
            pendingQualificationRequestsRef.current.delete(request);
          });
          if (seq !== requestSeqRef.current || !isMountedRef.current) return;
          if (response.success && response.data) {
            const enabledProducts = response.data.products.filter((product) =>
              isUnifiedPreSalesProductEnabled(product.product));
            setQualification({ ...response.data, products: enabledProducts });
            // Existing cards consume WP cancellation reasons; v3 calls that product HP.
            data = enabledProducts.flatMap((product) =>
              product.cancellationReasons.map((reason) => ({
                ...reason,
                product: product.product === 'HP' ? 'WP' : product.product,
              })),
            );
          }
        } else {
          data = cancellationEvaluationResults.get(requestKey) ?? null;
        }

        if (!unifiedFlow && !data) {
          let request = cancellationEvaluationRequests.get(requestKey);
          if (!request) {
            request = (async () => {
              const token = await GetAccessToken(instance, inProgress, scopes.salesAppointmentApi);
              const response = await heatBookingPageService.evaluatePreSalesCancellation(
                {
                  hpOrderNumber: hpOrderNumber!,
                  formValues: serializePreSalesAnswers(nextAnswers),
                  ...(lastChangedKey !== undefined && { lastChangedKey }),
                },
                token.accessToken,
              );

              if (!response.success || !response.data) {
                console.error('Cancellation evaluation failed', response);
                return null;
              }

              cacheCancellationEvaluationResult(requestKey, response.data);
              return response.data;
            })().finally(() => {
              cancellationEvaluationRequests.delete(requestKey);
            });
            cancellationEvaluationRequests.set(requestKey, request);
          }

          data = await request;
        }

        if (seq !== requestSeqRef.current || !isMountedRef.current) return;

        if (!data) {
          setCancellationEvaluationFailed(true);
          return;
        }

        const ordinaryHpCancellationReasons = getOrdinaryHpCancellationReasons(data);
        const hasOrdinaryHpCancellation = ordinaryHpCancellationReasons.length > 0;
        const isPvOnlyDisqualified = isPvOnlyInterest(nextAnswers.pv_interest)
          && data.some((reason) => reason.product === 'PV');
        setAnsweredQuestionKeysAtCancellation(new Set(
          hasOrdinaryHpCancellation || isPvOnlyDisqualified
            ? questions
              .filter((question) => !isPreSalesAnswerEmpty(nextAnswers[question.questionKey]))
              .map((question) => question.questionKey)
            : [],
        ));
        setCancellationReasons(data);
        setHasCompletedCancellationEvaluation(true);
        const ordinaryHpCancellationKey = JSON.stringify(
          ordinaryHpCancellationReasons,
        );
        if (ordinaryHpCancellationKey !== lastOrdinaryHpCancellationKeyRef.current) {
          setIsCancellationModalDismissed(false);
          lastOrdinaryHpCancellationKeyRef.current = ordinaryHpCancellationKey;
        }
        lastEvaluatedKeyRef.current = nextKey;
      } catch (error) {
        if (seq === requestSeqRef.current && isMountedRef.current) {
          console.error('Cancellation evaluation failed', error);
          setCancellationReasons([]);
          setCancellationEvaluationFailed(true);
        }
      } finally {
        if (seq === requestSeqRef.current && isMountedRef.current) {
          setIsEvaluatingCancellation(false);
        }
      }
    },
    [formId, hpOrderNumber, signupId, unifiedFlow, inProgress, instance, questions, isLocked],
  );

  useEffect(() => {
    if (
      answersSourceQuestions !== questions
      || hasRunInitialEvaluationRef.current
      || questions.length === 0
      || Object.keys(answers).length === 0
    ) return;
    hasRunInitialEvaluationRef.current = true;
    void evaluateCancellation(answers);
  }, [answers, answersSourceQuestions, evaluateCancellation, questions]);

  const setAnswer = useCallback((
    questionKey: string,
    value: string | number | string[] | undefined,
    shouldEvaluate = false,
  ) => {
    if (isLocked) return;
    const updatedAnswers = { ...answers, [questionKey]: value };
    const candidateAnswers = unifiedFlow
      ? updatedAnswers
      : applyPreSalesDerivedAnswers(questionKey, updatedAnswers);
    const {
      answers: nextAnswers,
      clearedQuestionKeys,
    } = clearNewlyHiddenPreSalesAnswers(questions, answers, candidateAnswers);

    if (unifiedFlow && getStablePreSalesAnswersKey(nextAnswers) === getStablePreSalesAnswersKey(answers)) return;

    setAnswers(nextAnswers);
    if (clearedQuestionKeys.length > 0) {
      setTouchedQuestions((current) => {
        const nextTouchedQuestions = { ...current };
        clearedQuestionKeys.forEach((key) => delete nextTouchedQuestions[key]);
        return nextTouchedQuestions;
      });
    }
    if (unifiedFlow) {
      // A pending response describes the answers before this edit, not the current form.
      requestSeqRef.current += 1;
      lastEvaluatedKeyRef.current = null;
      setIsEvaluatingCancellation(false);
      setHasCompletedCancellationEvaluation(false);
    }
    if (shouldEvaluate) {
      void evaluateCancellation(nextAnswers, questionKey);
    }
  }, [answers, evaluateCancellation, questions, unifiedFlow, isLocked]);

  const markTouched = useCallback((questionKey: string) => {
    setTouchedQuestions((current) => ({ ...current, [questionKey]: true }));
  }, []);

  const blurAnswer = useCallback((questionKey: string, shouldEvaluate = false) => {
    markTouched(questionKey);
    const customerInfoPatch = getPreSalesCustomerInfoPatch(
      questionKey,
      answers[questionKey],
    );
    if (customerInfoPatch) {
      onCustomerInfoChange?.(customerInfoPatch);
    }
    if (shouldEvaluate) {
      void evaluateCancellation(answers, questionKey);
    }
  }, [answers, evaluateCancellation, markTouched, onCustomerInfoChange, unifiedFlow]);

  const pvCancellation = useMemo(
    () => getPreSalesCancellationProjection(cancellationReasons, 'PV'),
    [cancellationReasons],
  );
  const ordinaryHpCancellation = useMemo(
    () => getPreSalesCancellationProjection(
      getOrdinaryHpCancellationReasons(cancellationReasons),
      'WP',
    ),
    [cancellationReasons],
  );
  const isPvOnly = !unifiedFlow && isPvOnlyInterest(answers.pv_interest);
  const shouldFreezeQuestions = !unifiedFlow && (ordinaryHpCancellation.reasons.length > 0
    || (isPvOnly && pvCancellation.reasons.length > 0));
  const visibleQuestions = useMemo(
    () => questions.filter((question) =>
      evaluatePreSalesQuestionVisible(question, answers)
      && (!isPvOnly || isPvOrSharedQuestion(question))
      && (
        !shouldFreezeQuestions
        || answeredQuestionKeysAtCancellation.has(question.questionKey)
      )),
    [answeredQuestionKeysAtCancellation, answers, isPvOnly, questions, shouldFreezeQuestions],
  );
  const cancellationQuestions = useMemo(
    () => questions.filter(
      (question) => ordinaryHpCancellation.questionKeys.includes(question.questionKey),
    ),
    [ordinaryHpCancellation.questionKeys, questions],
  );

  const areVisibleQuestionsValid = useMemo(
    () => visibleQuestions.every((question) => !evaluatePreSalesQuestionValidation(
      question,
      answers[question.questionKey],
      answers,
      true,
    )),
    [answers, visibleQuestions],
  );

  const hasNoBuildableProduct = unifiedFlow
    && hasCompletedCancellationEvaluation
    && qualification !== undefined
    && !qualification.products.some((product) => product.status === 'Buildable');

  const validateVisibleQuestions = useCallback(() => {
    const touchedVisibleQuestions = visibleQuestions.reduce<Record<string, boolean>>((acc, question) => {
      acc[question.questionKey] = true;
      return acc;
    }, {});
    setTouchedQuestions((current) => ({ ...current, ...touchedVisibleQuestions }));

    const firstInvalidQuestion = visibleQuestions.find((question) =>
      Boolean(evaluatePreSalesQuestionValidation(
        question,
        answers[question.questionKey],
        answers,
        true,
      )),
    );

    if (firstInvalidQuestion) {
      scrollToPreSalesQuestion(firstInvalidQuestion.questionKey);
      return false;
    }

    return true;
  }, [answers, visibleQuestions]);

  const prepareForReturnToForm = useCallback(() => {
    const currentAnswersKey = getStablePreSalesAnswersKey(answers);
    const needsCancellationEvaluation =
      currentAnswersKey !== lastEvaluatedKeyRef.current
      || !hasCompletedCancellationEvaluation
      || cancellationEvaluationFailed;

    requestSeqRef.current += 1;
    setIsEvaluatingCancellation(false);

    if (needsCancellationEvaluation) {
      void evaluateCancellation(answers);
    }
  }, [
    answers,
    cancellationEvaluationFailed,
    evaluateCancellation,
    hasCompletedCancellationEvaluation,
  ]);

  const prepareForSubmit = useCallback(async () => {
    // Finish earlier saves before the submission endpoint qualifies and saves the final answers.
    requestSeqRef.current += 1;
    setIsEvaluatingCancellation(false);
    await Promise.allSettled([...pendingQualificationRequestsRef.current]);
  }, []);

  const cancellationEvaluationStatus: PreSalesCancellationEvaluationStatus = isEvaluatingCancellation
    ? 'loading'
    : cancellationEvaluationFailed
      ? 'error'
      : lastEvaluatedKeyRef.current !== null
        ? 'success'
        : 'idle';

  return {
    questions,
    qualification,
    answers,
    touchedQuestions,
    visibleQuestions,
    areVisibleQuestionsValid,
    hasNoBuildableProduct,
    cancellationReasons,
    pvCancellation,
    ordinaryHpCancellation,
    cancellationQuestions,
    isCancellationModalDismissed,
    hasCompletedCancellationEvaluation,
    isEvaluatingCancellation,
    cancellationEvaluationFailed,
    cancellationEvaluationStatus,
    setAnswer,
    markTouched,
    blurAnswer,
    validateVisibleQuestions,
    prepareForReturnToForm,
    prepareForSubmit,
    dismissCancellationModal: () => setIsCancellationModalDismissed(true),
  };
};
